import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { api, apiError } from "../lib/api";
import { readSseText } from "../lib/stream";
import { useProfile } from "../lib/queries";

/**
 * The prep card — the WhatsApp message before a conversation that matters.
 *
 * The card is composed against the curated base: when the org she is meeting
 * matches a verified entry, the card says so; when it does not, it says that
 * too. An unmapped room gets different advice, never invented facts.
 */
export const Route = createFileRoute("/prep")({
  component: Prep,
});

/** WhatsApp's own markup: *bold* and _italic_. Rendered rather than shown
 *  raw, because this is a preview of the message she is about to paste. */
function whatsapp(text: string) {
  return text.split(/(\*[^*\n]+\*|_[^_\n]+_)/g).map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <b key={i}>{part.slice(1, -1)}</b>;
    }
    if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
      return <i key={i}>{part.slice(1, -1)}</i>;
    }
    return <span key={i}>{part}</span>;
  });
}

function Prep() {
  const profile = useProfile();

  const [person, setPerson] = useState("");
  const [org, setOrg] = useState("");
  const [when, setWhen] = useState("");
  const [context, setContext] = useState("");

  const [card, setCard] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [mode, setMode] = useState<string | null>(null);
  const [matchedOffer, setMatchedOffer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function generate() {
    setStreaming(true);
    setError(null);
    setCard("");
    setCopied(false);
    try {
      const res = await api["prep-card"].$post({
        json: {
          meeting: {
            person: person.trim(),
            org: org.trim(),
            when: when.trim(),
            ...(context.trim() ? { context: context.trim() } : {}),
          },
        },
      });
      if (!res.ok) throw await apiError(res);

      // Both headers arrive before the first token — which is the point of
      // sending them as headers rather than in the body.
      setMode(res.headers.get("x-omina-mode"));
      setMatchedOffer(res.headers.get("x-omina-offer"));

      await readSseText(res.body, (delta) => setCard((current) => current + delta));
    } catch (e) {
      setError(e instanceof Error ? e.message : "something went wrong");
    } finally {
      setStreaming(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
    } catch {
      // Denied or an insecure origin — the card is on screen and selectable.
      setError("Couldn't reach the clipboard — select the card and copy it.");
    }
  }

  const canGenerate = Boolean(person.trim()) && Boolean(org.trim()) && !streaming;

  return (
    <>
      <div className="eyebrow">Follow up</div>
      <h1 className="tk">Never walk in unprepared.</h1>
      <p className="lede">
        Tell us who the conversation is with. You get the card: who they are,
        your opener, the question that is coming, where you slip, how you
        decide. Copy it into WhatsApp.
      </p>

      {profile.isSuccess && !profile.data && (
        <div className="samplebar">
          The card is built from your profile.{" "}
          <Link to="/intake">Do the intake first →</Link>
        </div>
      )}

      <div className="card">
        <div className="field" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label className="lbl" htmlFor="person">
              Who you&rsquo;re meeting
            </label>
            <input
              id="person"
              className="tk"
              value={person}
              onChange={(e) => setPerson(e.target.value)}
              placeholder="Sarah Chen"
            />
          </div>
          <div>
            <label className="lbl" htmlFor="org">
              Company / fund
            </label>
            <input
              id="org"
              className="tk"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              placeholder="Restive Ventures"
            />
          </div>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="when">
            When <span className="muted">(free text, optional)</span>
          </label>
          <input
            id="when"
            className="tk"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            placeholder="Tue 2pm"
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="prep-context">
            Anything you know about them <span className="muted">(optional)</span>
          </label>
          <textarea
            id="prep-context"
            className="tk"
            rows={2}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Intro'd by João. She led the seed in a fintech-infra company last year."
          />
        </div>

        <button
          type="button"
          className="btn"
          disabled={!canGenerate}
          onClick={() => void generate()}
        >
          {streaming ? "Building the card…" : "Generate prep card"}
        </button>

        {error && <div className="danger">{error}</div>}
      </div>

      {(card || streaming) && (
        <div style={{ marginTop: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            The card
            {mode === "draft" ? " · template mode (no model key — from your profile)" : ""}
            {matchedOffer
              ? " · matched a verified entry in the base"
              : card && !streaming
                ? " · unmapped room"
                : ""}
          </div>

          {/* Streamed straight into the preview, so she watches it compose
              rather than watching a spinner. */}
          <div className={`wa${streaming ? " caret" : ""}`}>
            {card ? whatsapp(card) : "…"}
          </div>

          <button type="button" className="btn" disabled={streaming || !card} onClick={() => void copy()}>
            {copied ? "Copied ✓" : "Copy for WhatsApp"}
          </button>

          <p className="hint">
            Paste it ~2h before the call. The three debrief questions at the
            bottom are what make the next card sharper.
          </p>
        </div>
      )}
    </>
  );
}
